import { CommonModule } from '@angular/common'
import { Component, computed, inject, input } from '@angular/core'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'

import { isNil as _isNil } from 'lodash/fp'

import { FlxIconModule } from '@flowx/angular-ui-toolkit'
import { ChangeEntity } from '@flowx/designer/modules/ai/types/ai.types'
import { SummaryIcons } from '@flowx/designer/modules/bpmn-editor/fabric.constants'
import { BoundaryEventType } from '@flowx/designer/shared/entities/boundary-event/boundary-event.common'
import {
  ActivityNode,
  EventNodes,
  GatewayNodes,
  NodeType,
  Nodes,
} from '@flowx/designer/shared/entities/node.types'

const summaryIconsMap: Map<NodeType | BoundaryEventType, string> = new Map([
  [ActivityNode.USER_TASK, SummaryIcons.userTask],
  [ActivityNode.TASK, SummaryIcons.serviceTask],
  [ActivityNode.SUBPROCESS_RUN, SummaryIcons.callActivity],
  [ActivityNode.SUBPROCESS_EMBED, SummaryIcons.embeddedSubprocess],
  [ActivityNode.MESSAGE_EVENT_SEND, SummaryIcons.sendMessage],
  [ActivityNode.MESSAGE_EVENT_RECEIVE, SummaryIcons.receiveMessage],
  [GatewayNodes.GATEWAY_EXCLUSIVE, SummaryIcons.gatewayExclusive],
  [GatewayNodes.GATEWAY_PARALLEL, SummaryIcons.gatewayInclusive],
  [EventNodes.START_EVENT, SummaryIcons.startProcess],
  [EventNodes.END_EVENT, SummaryIcons.endProcess],
  [EventNodes.START_MESSAGE_CATCH_EVENT, SummaryIcons.startCatchMessageEvent],
  [EventNodes.INTERMEDIATE_MESSAGE_CATCH_EVENT, SummaryIcons.catchInterruptingMessageEvent],
  [EventNodes.INTERMEDIATE_MESSAGE_THROW_EVENT, SummaryIcons.throwMessageEvent],
  [EventNodes.START_TIMER_EVENT, SummaryIcons.startTimerEvent],
  [EventNodes.INTERMEDIATE_TIMER_EVENT, SummaryIcons.InterruptingTimerEvent],
  [EventNodes.START_MILESTONE, ''],
  [EventNodes.END_MILESTONE, ''],
  [BoundaryEventType.MESSAGE_THROW, SummaryIcons.throwMessageEvent],
  [BoundaryEventType.MESSAGE_CATCH_INTERRUPTING, SummaryIcons.catchInterruptingMessageEvent],
  [BoundaryEventType.MESSAGE_CATCH_NON_INTERRUPTING, SummaryIcons.catchNonInterruptingMessageEvent],
  [BoundaryEventType.TIMER_INTERRUPTING, SummaryIcons.InterruptingTimerEvent],
  [BoundaryEventType.TIMER_NON_INTERRUPTING, SummaryIcons.nonInterruptingTimerEvent],
  [BoundaryEventType.ERROR_CATCH_INTERRUPTING, SummaryIcons.catchInterruptingMessageEvent],
] as [NodeType | BoundaryEventType, string][])

@Component({
  selector: 'flx-ai-analyst-summary',
  templateUrl: './ai-analyst-summary.component.html',
  styleUrl: './ai-analyst-summary.component.scss',
  imports: [CommonModule, FlxIconModule],
})
export class AnalystSummaryComponent {
  #sanitizer = inject(DomSanitizer)

  added = input<ChangeEntity[]>([])
  removed = input<ChangeEntity[]>([])

  addedWithDisplayMetadata = computed(() => this.#mapEntitiesToDisplayMetadata(this.added()))
  removedWithDisplayMetadata = computed(() => this.#mapEntitiesToDisplayMetadata(this.removed()))

  showMore = false

  nodeIconByType = Object.entries({
    ...Nodes,
    ...BoundaryEventType,
  }).reduce(
    (nodeIconsMap, [nodeKey, nodeType]) => {
      const iconSvg = summaryIconsMap.get(nodeType)
      const iconSvgColorized = iconSvg?.replaceAll(
        /(?<=<path\b[^<>]*)\s*\bfill=(["']).*?\1/g,
        ` fill="currentColor"`
      )

      nodeIconsMap[nodeKey as NodeType] = this.#sanitizer.bypassSecurityTrustHtml(iconSvgColorized)

      return nodeIconsMap
    },
    {} as Record<NodeType, SafeHtml>
  )

  #mapEntitiesToDisplayMetadata(entities: ChangeEntity[]): (ChangeEntity & {
    italic: boolean
  })[] {
    return entities.map((entity) => ({
      ...entity,
      italic: !entity.name,
      name:
        entity.name === '' || _isNil(entity.name) ? `[${entity.type.toLowerCase()}]` : entity.name,
    }))
  }
}
